import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { invoicesApi, ordersApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  FileText, Search, Users, List, IndianRupee, Clock, AlertTriangle,
  CheckCircle2, ArrowUpRight, ChevronDown, ChevronUp, Plus, Loader2,
  Calendar, CreditCard, ArrowLeftRight, Filter, Ban
} from 'lucide-react';
import HomeButton from '../components/HomeButton';

const formatCurrency = (v) => {
  if (!v && v !== 0) return '-';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v);
};

const formatDate = (iso) => {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const daysFromNow = (iso) => {
  if (!iso) return null;
  const diff = Math.ceil((new Date(iso) - new Date()) / (1000 * 60 * 60 * 24));
  return diff;
};

const StatusBadge = ({ status }) => {
  const styles = {
    paid: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400 border-emerald-200',
    partial: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400 border-amber-200',
    pending: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400 border-red-200',
  };
  const labels = { paid: 'Paid', partial: 'Partial', pending: 'Pending' };
  return (
    <Badge variant="outline" className={`text-[10px] ${styles[status] || styles.pending}`} data-testid={`status-badge-${status}`}>
      {labels[status] || status}
    </Badge>
  );
};

const DueDateIndicator = ({ dueDate, label }) => {
  const days = daysFromNow(dueDate);
  if (days === null) return null;
  const isOverdue = days < 0;
  const isDueSoon = days >= 0 && days <= 3;
  return (
    <div className={`text-[10px] flex items-center gap-1 ${isOverdue ? 'text-red-600 dark:text-red-400 font-semibold' : isDueSoon ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'}`}>
      {isOverdue ? <AlertTriangle className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
      {label}: {formatDate(dueDate)} {isOverdue ? `(${Math.abs(days)}d overdue)` : days === 0 ? '(today)' : `(${days}d)`}
    </div>
  );
};

export default function Invoices() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('customers'); // 'customers' | 'all'
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedCustomers, setExpandedCustomers] = useState({});

  // Payment dialog
  const [paymentDialog, setPaymentDialog] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ payment_type: 'metal', amount: '', payment_mode: 'rtgs', reference_number: '', date: new Date().toISOString().slice(0, 10), notes: '' });
  const [savingPayment, setSavingPayment] = useState(false);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const { data } = await invoicesApi.getAll();
      setInvoices(data);
    } catch (err) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    let list = invoices;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(i =>
        i.customer_name?.toLowerCase().includes(q) ||
        i.invoice_number?.toLowerCase().includes(q) ||
        i.order_number?.toLowerCase().includes(q) ||
        i.co_number?.toLowerCase().includes(q)
      );
    }
    if (statusFilter !== 'all') {
      list = list.filter(i => i.payment_status === statusFilter);
    }
    return list;
  }, [invoices, search, statusFilter]);

  // Customer-grouped view
  const customerGroups = useMemo(() => {
    const groups = {};
    for (const inv of filtered) {
      const name = inv.customer_name || 'Unknown';
      if (!groups[name]) groups[name] = { customer: name, invoices: [], total_value: 0, total_pending: 0, total_paid: 0 };
      groups[name].invoices.push(inv);
      groups[name].total_value += inv.total_value;
      groups[name].total_pending += inv.total_pending;
      groups[name].total_paid += inv.total_paid;
    }
    return Object.values(groups).sort((a, b) => b.total_pending - a.total_pending);
  }, [filtered]);

  // Summary KPIs
  const summary = useMemo(() => {
    const total = filtered.reduce((s, i) => s + i.total_value, 0);
    const pending = filtered.reduce((s, i) => s + i.total_pending, 0);
    const paid = filtered.reduce((s, i) => s + i.total_paid, 0);
    const overdue = filtered.filter(i => {
      if (i.payment_status === 'paid') return false;
      const md = daysFromNow(i.metal_due_date);
      const dd = daysFromNow(i.diamond_due_date);
      return (md !== null && md < 0) || (dd !== null && dd < 0);
    }).length;
    return { total, pending, paid, overdue, count: filtered.length };
  }, [filtered]);

  const toggleCustomer = (name) => {
    setExpandedCustomers(p => ({ ...p, [name]: !p[name] }));
  };

  const openPaymentDialog = (inv) => {
    setPaymentDialog(inv);
    setPaymentForm({
      payment_type: inv.metal_pending > 0 ? 'metal' : 'diamond_others',
      amount: '',
      payment_mode: 'rtgs',
      reference_number: '',
      date: new Date().toISOString().slice(0, 10),
      notes: '',
      metal_exchange_weight: '',
      metal_exchange_invoice_number: '',
    });
  };

  const handleRecordPayment = async () => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    if (paymentForm.payment_mode === 'metal_exchange' && !paymentForm.metal_exchange_weight) {
      toast.error('Enter the metal exchange weight');
      return;
    }
    setSavingPayment(true);
    try {
      await ordersApi.recordPayment(paymentDialog.order_id, paymentDialog.dispatch_id, {
        ...paymentForm,
        amount: parseFloat(paymentForm.amount),
        metal_exchange_weight: parseFloat(paymentForm.metal_exchange_weight) || 0,
      });
      toast.success('Payment recorded');
      setPaymentDialog(null);
      fetchInvoices();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSavingPayment(false);
    }
  };

  // Render an invoice row
  const InvoiceRow = ({ inv, showCustomer = false }) => (
    <div
      className="grid grid-cols-12 gap-2 items-center px-4 py-3 border-b last:border-0 hover:bg-muted/30 transition-colors text-sm cursor-pointer"
      onClick={() => navigate(`/orders/${inv.order_id}`)}
      data-testid={`invoice-row-${inv.dispatch_id}`}
    >
      <div className="col-span-3 md:col-span-2">
        <p className="font-semibold text-xs">{inv.invoice_number || '-'}</p>
        {showCustomer && <p className="text-[10px] text-muted-foreground truncate">{inv.customer_name}</p>}
        <p className="text-[10px] text-muted-foreground">{inv.order_number}</p>
      </div>
      <div className="col-span-2 hidden md:block">
        <p className="text-xs">{inv.gold_weight ? `${inv.gold_weight}g ${inv.gold_karat}` : '-'}</p>
        <p className="text-[10px] text-muted-foreground">{inv.diamond_weight ? `${inv.diamond_weight} ct` : ''}</p>
      </div>
      <div className="col-span-2">
        <p className="text-xs font-medium">{formatCurrency(inv.total_value)}</p>
        <p className="text-[10px] text-muted-foreground">
          M: {formatCurrency(inv.metal_value)} | D: {formatCurrency(inv.diamond_others_value)}
        </p>
      </div>
      <div className="col-span-2">
        <p className="text-xs font-semibold text-red-600 dark:text-red-400">{formatCurrency(inv.total_pending)}</p>
        {inv.has_metal_exchange && (
          <span className="text-[9px] text-amber-600 flex items-center gap-0.5"><ArrowLeftRight className="h-2.5 w-2.5" /> Metal Exch.</span>
        )}
      </div>
      <div className="col-span-2 hidden md:block">
        {inv.payment_type === 'cod' ? (
          <span className="text-[10px] text-muted-foreground">COD</span>
        ) : (
          <div className="space-y-0.5">
            <DueDateIndicator dueDate={inv.metal_due_date} label="Metal" />
            <DueDateIndicator dueDate={inv.diamond_due_date} label="D&O" />
          </div>
        )}
      </div>
      <div className="col-span-3 md:col-span-2 flex items-center gap-2 justify-end">
        <StatusBadge status={inv.payment_status} />
        {inv.payment_status !== 'paid' && (
          <Button
            size="sm"
            variant="outline"
            className="h-7 px-2 text-[10px]"
            onClick={(e) => { e.stopPropagation(); openPaymentDialog(inv); }}
            data-testid={`record-payment-${inv.dispatch_id}`}
          >
            <Plus className="h-3 w-3 mr-0.5" /> Pay
          </Button>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="invoices-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <HomeButton />
          <div>
            <h1 className="text-2xl font-heading font-bold tracking-tight">Invoices & Debtors</h1>
            <p className="text-sm text-muted-foreground">{summary.count} invoices across {customerGroups.length} customers</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant={view === 'customers' ? 'default' : 'outline'} size="sm" onClick={() => setView('customers')} data-testid="view-customers">
            <Users className="h-4 w-4 mr-1" /> By Customer
          </Button>
          <Button variant={view === 'all' ? 'default' : 'outline'} size="sm" onClick={() => setView('all')} data-testid="view-all">
            <List className="h-4 w-4 mr-1" /> All Invoices
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-gradient-to-br from-background to-muted/30">
          <CardContent className="p-4">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Total Invoiced</p>
            <p className="text-xl font-bold font-heading mt-1" data-testid="kpi-total">{formatCurrency(summary.total)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-background to-emerald-500/5">
          <CardContent className="p-4">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Received</p>
            <p className="text-xl font-bold font-heading mt-1 text-emerald-600 dark:text-emerald-400" data-testid="kpi-paid">{formatCurrency(summary.paid)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-background to-red-500/5">
          <CardContent className="p-4">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Outstanding</p>
            <p className="text-xl font-bold font-heading mt-1 text-red-600 dark:text-red-400" data-testid="kpi-pending">{formatCurrency(summary.pending)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-background to-amber-500/5">
          <CardContent className="p-4">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Overdue</p>
            <p className="text-xl font-bold font-heading mt-1 text-amber-600 dark:text-amber-400" data-testid="kpi-overdue">{summary.overdue}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search customer, invoice #, order #..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="invoice-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px]" data-testid="status-filter">
            <Filter className="h-4 w-4 mr-1" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="partial">Partial</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Main Content */}
      {filtered.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-muted-foreground">
            <FileText className="h-12 w-12 mb-3 opacity-40" />
            <p className="font-medium">No invoices found</p>
            <p className="text-sm mt-1">Invoices will appear here when orders are dispatched with invoices.</p>
          </CardContent>
        </Card>
      ) : view === 'customers' ? (
        /* Customer Grouped View */
        <div className="space-y-3" data-testid="customer-view">
          {customerGroups.map(group => (
            <Card key={group.customer} className="overflow-hidden">
              <button
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors text-left"
                onClick={() => toggleCustomer(group.customer)}
                data-testid={`customer-group-${group.customer}`}
              >
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-xs font-bold text-primary">{group.customer.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="font-semibold text-sm">{group.customer}</p>
                    <p className="text-[10px] text-muted-foreground">{group.invoices.length} invoice{group.invoices.length > 1 ? 's' : ''}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Outstanding</p>
                    <p className={`text-sm font-bold ${group.total_pending > 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                      {formatCurrency(group.total_pending)}
                    </p>
                  </div>
                  <div className="text-right hidden sm:block">
                    <p className="text-xs text-muted-foreground">Total</p>
                    <p className="text-sm font-medium">{formatCurrency(group.total_value)}</p>
                  </div>
                  {expandedCustomers[group.customer] ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </div>
              </button>
              {expandedCustomers[group.customer] && (
                <div className="border-t">
                  {/* Table Header */}
                  <div className="grid grid-cols-12 gap-2 px-4 py-2 text-[10px] uppercase tracking-wider text-muted-foreground bg-muted/20 font-medium">
                    <div className="col-span-3 md:col-span-2">Invoice</div>
                    <div className="col-span-2 hidden md:block">Weight</div>
                    <div className="col-span-2">Value</div>
                    <div className="col-span-2">Pending</div>
                    <div className="col-span-2 hidden md:block">Due Dates</div>
                    <div className="col-span-3 md:col-span-2 text-right">Status</div>
                  </div>
                  {group.invoices.map(inv => (
                    <InvoiceRow key={inv.dispatch_id} inv={inv} />
                  ))}
                </div>
              )}
            </Card>
          ))}
        </div>
      ) : (
        /* All Invoices View */
        <Card className="overflow-hidden" data-testid="all-invoices-view">
          <div className="grid grid-cols-12 gap-2 px-4 py-2 text-[10px] uppercase tracking-wider text-muted-foreground bg-muted/20 font-medium border-b">
            <div className="col-span-3 md:col-span-2">Invoice</div>
            <div className="col-span-2 hidden md:block">Weight</div>
            <div className="col-span-2">Value</div>
            <div className="col-span-2">Pending</div>
            <div className="col-span-2 hidden md:block">Due Dates</div>
            <div className="col-span-3 md:col-span-2 text-right">Status</div>
          </div>
          {filtered.sort((a, b) => {
            // Sort: overdue first, then by total_pending desc
            const aOverdue = (daysFromNow(a.metal_due_date) < 0 || daysFromNow(a.diamond_due_date) < 0) && a.payment_status !== 'paid';
            const bOverdue = (daysFromNow(b.metal_due_date) < 0 || daysFromNow(b.diamond_due_date) < 0) && b.payment_status !== 'paid';
            if (aOverdue && !bOverdue) return -1;
            if (!aOverdue && bOverdue) return 1;
            return b.total_pending - a.total_pending;
          }).map(inv => (
            <InvoiceRow key={inv.dispatch_id} inv={inv} showCustomer />
          ))}
        </Card>
      )}

      {/* Record Payment Dialog */}
      <Dialog open={!!paymentDialog} onOpenChange={(v) => { if (!savingPayment && !v) setPaymentDialog(null); }}>
        <DialogContent className="max-w-sm" onClick={e => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-emerald-500" /> Record Payment
            </DialogTitle>
            <DialogDescription>
              {paymentDialog?.invoice_number} — {paymentDialog?.customer_name}
            </DialogDescription>
          </DialogHeader>
          {paymentDialog && (
            <div className="space-y-3 py-2">
              {/* Outstanding summary */}
              <div className="grid grid-cols-2 gap-2 p-3 rounded-lg bg-muted/40 text-xs">
                <div>
                  <p className="text-muted-foreground">Metal Pending</p>
                  <p className="font-bold text-red-600 dark:text-red-400">{formatCurrency(paymentDialog.metal_pending)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">D&O Pending</p>
                  <p className="font-bold text-red-600 dark:text-red-400">{formatCurrency(paymentDialog.diamond_pending)}</p>
                </div>
              </div>
              <div className="space-y-1">
                <Label>Payment For</Label>
                <Select value={paymentForm.payment_type} onValueChange={v => setPaymentForm(p => ({...p, payment_type: v}))}>
                  <SelectTrigger data-testid="payment-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {paymentDialog.metal_pending > 0 && (
                      <SelectItem value="metal">Metal</SelectItem>
                    )}
                    <SelectItem value="diamond_others">Diamond & Others</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Amount (Rs) *</Label>
                <Input type="number" value={paymentForm.amount} onChange={e => setPaymentForm(p => ({...p, amount: e.target.value}))} placeholder="0" data-testid="payment-amount" />
              </div>
              <div className="space-y-1">
                <Label>Payment Mode</Label>
                <Select value={paymentForm.payment_mode} onValueChange={v => setPaymentForm(p => ({...p, payment_mode: v}))}>
                  <SelectTrigger data-testid="payment-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rtgs">RTGS/NEFT</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="metal_exchange">Metal Exchange</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {paymentForm.payment_mode === 'metal_exchange' && (
                <div className="grid grid-cols-2 gap-3 pl-4 border-l-2 border-amber-300">
                  <div className="space-y-1">
                    <Label>Metal Weight (gm, 999) *</Label>
                    <Input type="number" step="0.001" value={paymentForm.metal_exchange_weight} onChange={e => setPaymentForm(p => ({...p, metal_exchange_weight: e.target.value}))} placeholder="0.000" data-testid="payment-exchange-weight" />
                  </div>
                  <div className="space-y-1">
                    <Label>Customer Invoice #</Label>
                    <Input value={paymentForm.metal_exchange_invoice_number} onChange={e => setPaymentForm(p => ({...p, metal_exchange_invoice_number: e.target.value}))} placeholder="Sales inv #" data-testid="payment-exchange-invoice" />
                  </div>
                </div>
              )}
              <div className="space-y-1">
                <Label>Reference / UTR</Label>
                <Input value={paymentForm.reference_number} onChange={e => setPaymentForm(p => ({...p, reference_number: e.target.value}))} placeholder="UTR / Cheque No." data-testid="payment-reference" />
              </div>
              <div className="space-y-1">
                <Label>Date</Label>
                <Input type="date" value={paymentForm.date} onChange={e => setPaymentForm(p => ({...p, date: e.target.value}))} data-testid="payment-date" />
              </div>
              <div className="space-y-1">
                <Label>Notes</Label>
                <Input value={paymentForm.notes} onChange={e => setPaymentForm(p => ({...p, notes: e.target.value}))} placeholder="Optional notes" data-testid="payment-notes" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentDialog(null)}>Cancel</Button>
            <Button onClick={handleRecordPayment} disabled={savingPayment} data-testid="submit-payment">
              {savingPayment && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
